import { useCallback } from 'react';
import { PreferenesDTO } from 'business/models/Models';
import { GlobalPreferencesService } from 'business/services/GlobalPreferencesService';
import { waitForHitCountRecalculation } from 'business/services/hitCountRecalculationTaskService';
import { LongRunningTask } from 'components/systemTasks/SystemTask';
import { PropertyConstants } from 'enums/PropertyConstants';
import { useRMDispatch } from 'redux/hooks';
import { addTask } from 'redux/reducers/systemTaskReducer';
import { IGNORE_AUTO_SCORE_MESSAGES } from 'constants/messages';
import { getNewId, showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import useBroadcast, { CHANNEL_ACTIONS, CHANNEL_TYPES } from 'business/broadcast';
import { systemPropertiesCache } from 'generic/systemPropertiesCache';

type SavePreferencesParams = {
  updatedProperties: any[];
  preferenceType: string;
  fetchUserPrefs?: () => void;
  onSuccess?: () => void;
  onFinally?: () => void;
};

export const useSavePreferences = () => {
  const dispatch = useRMDispatch();
  const preferencesBroadcastChannel = useBroadcast(CHANNEL_TYPES.PREFERENCES);

  const savePreferences = useCallback(
    async (params: SavePreferencesParams) => {
      const { updatedProperties, preferenceType, onSuccess, onFinally } = params;

      try {
        const service = new GlobalPreferencesService();
        const preferences = new PreferenesDTO();
        preferences.baseProperties = updatedProperties;
        preferences.preferenceType = preferenceType;
        const isSuccess = await service.setAllProperties(preferences);
        if (isSuccess) {
          updatedProperties.forEach((prop) => systemPropertiesCache.clear(prop.name));
          preferencesBroadcastChannel?.postMessage({
            action: CHANNEL_ACTIONS.PROPERTY_UPDATE,
            payload: { propertyNames: updatedProperties.map((prop) => prop.name) },
          });
          showAlertSnackBar(EAlertType.SUCCESS, ESnackBarPosition.TOP, 'Changes saved.');
          if (
            updatedProperties.some(
              (prop) => prop.name === PropertyConstants.ExcludeIgnoredScoresFromHitCounts,
            )
          ) {
            showAlertSnackBar(
              EAlertType.INFO,
              ESnackBarPosition.TOP,
              IGNORE_AUTO_SCORE_MESSAGES.hitCountRecalculation,
            );
            dispatch(
              addTask({
                id: getNewId(),
                title: 'Recalculating hit counts',
                completionTitle: 'Hit counts recalculated',
                param: [],
                serviceMathod: waitForHitCountRecalculation,
                progress: 1,
              } as LongRunningTask),
            );
          }
          onSuccess && onSuccess();
        } else {
          showAlertSnackBar(
            EAlertType.ERROR,
            ESnackBarPosition.TOP,
            'Failed to save changes.',
          );
        }
      } catch {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'An error occured while saving the changes.',
        );
      } finally {
        onFinally && onFinally();
      }
    },
    [dispatch, preferencesBroadcastChannel],
  );

  return { savePreferences };
};
